<!--
  Shift picker — "who is on shift".

  This is the app's substitute for per-person accounts: the chosen name stamps
  every entry and every audit row, reproducing the paper's "SCI Initials / Time"
  column. It persists on the device, so it is picked once a day at most.

  Only crew sessions see it; a client records nothing, so asking a client who
  is on shift would be noise.
-->
<script lang="ts">
  import { isApiError } from '../lib/api';
  import {
    chooseShift,
    forgetShiftName,
    knownShiftNames,
    lastShiftName,
    project
  } from '../lib/store';

  let newName = $state('');
  let busy = $state<string | null>(null);
  let error = $state<string | null>(null);
  let managing = $state(false);

  const suggested = lastShiftName();

  async function pick(name: string) {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    error = null;
    busy = trimmed;
    try {
      await chooseShift(trimmed);
    } catch (cause) {
      error = isApiError(cause) ? cause.message : 'Could not set the shift name.';
    } finally {
      busy = null;
    }
  }

  function addName(event: SubmitEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    newName = '';
    void pick(name);
  }
</script>

<div class="screen">
  <div class="head">
    <h1>Who is on shift?</h1>
    <p class="meta">
      {$project?.name ?? 'This project'} — every entry you make is stamped with this name and the
      server's time.
    </p>
  </div>

  {#if error}
    <div class="banner banner--error" role="alert"><div>{error}</div></div>
  {/if}

  {#if $knownShiftNames.length > 0}
    <div class="card">
      <ul class="list">
        {#each $knownShiftNames as name (name)}
          <li>
            <div class="nameline">
              <button
                class="list__item namebtn"
                type="button"
                onclick={() => pick(name)}
                disabled={busy !== null}
              >
                <span class="namebtn__label">{name}</span>
                {#if busy === name}
                  <span class="spinner" aria-hidden="true"></span>
                {:else if name === suggested}
                  <span class="pill pill--brand">Last used</span>
                {/if}
              </button>
              {#if managing}
                <button
                  class="btn btn--sm btn--danger remove"
                  type="button"
                  onclick={() => forgetShiftName(name)}
                  aria-label={`Remove ${name} from this device`}
                >
                  Remove
                </button>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    </div>

    <button class="btn btn--ghost btn--block" type="button" onclick={() => (managing = !managing)}>
      {managing ? 'Done' : 'Edit the name list'}
    </button>
  {/if}

  <form class="card" onsubmit={addName}>
    <div class="card__body">
      <label class="field">
        <span class="label">{$knownShiftNames.length > 0 ? 'Someone else' : 'Your name'}</span>
        <input
          type="text"
          bind:value={newName}
          autocapitalize="words"
          autocomplete="name"
          enterkeyhint="go"
          placeholder="First name and last initial"
        />
      </label>
      <button
        class="btn btn--primary btn--block btn--big"
        type="submit"
        disabled={busy !== null || newName.trim() === ''}
      >
        Start shift
      </button>
    </div>
  </form>
</div>

<style>
  .head {
    padding: 1.5rem 0 1rem;
  }

  .nameline {
    display: flex;
    align-items: stretch;
  }

  .namebtn {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-height: 3.25rem;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .namebtn__label {
    flex: 1 1 auto;
  }

  .remove {
    flex: 0 0 auto;
    margin: 0.4rem 0.6rem 0.4rem 0;
  }
</style>
